import { useSession } from '@zhiyue/core';
import { Alert, Button, Spin } from 'antd';
import React from 'react';
import {
  createIic,
  getIicAnalytics,
  getIicWorkbench,
  listIic,
  runIicCommand,
  updateIic,
} from '@/services/bff/iic.api';
import { getCurrentMemberUuid } from '@/utils/currentMember';
import {
  normalizeRecord,
  resourceForType,
  serializeRecord,
  stageToCode,
} from './mappers';
import type {
  ActivityLog,
  BaseEntity,
  EntityQuery,
  EntityType,
  IicState,
  InvestmentStage,
  LandingCompletionInput,
  ServiceCompletionInput,
} from './types';

const emptyState = (): IicState => ({
  activities: [],
  leads: [],
  enterprises: [],
  contacts: [],
  investmentProjects: [],
  landingProjects: [],
  enterpriseServices: [],
  carriers: [],
  policyFunds: [],
  cooperationResources: [],
  tasks: [],
  activitiesLog: [],
  tenantObjectProfiles: [],
  workbench: null,
  analytics: null,
  industryChains: [],
  industrySegments: [],
  industryEnterprises: [],
});

const page = { page: 1, pageSize: 100 };
const mapItems = (items: Record<string, any>[]) =>
  items.map(normalizeRecord) as any[];

const fetchState = async (): Promise<IicState> => {
  const [
    activities,
    leads,
    enterprises,
    contacts,
    projects,
    landings,
    services,
    resources,
    tasks,
    logs,
    projectNeeds,
    resourceMatches,
    profiles,
    workbench,
    analytics,
    industryChains,
    industrySegments,
    industryEnterprises,
  ] = await Promise.all([
    listIic('activities', page),
    listIic('leads', page),
    listIic('enterprises', page),
    listIic('contacts', page),
    listIic('investment-projects', page),
    listIic('landing-projects', page),
    listIic('enterprise-services', page),
    listIic('resources', page),
    listIic('tasks', page),
    listIic('activity-logs', page),
    listIic('project-needs', page),
    listIic('resource-matches', page),
    listIic('tenant-object-profiles', page),
    getIicWorkbench(),
    getIicAnalytics(),
    listIic('industry-chains', page),
    listIic('industry-segments', page),
    listIic('industry-enterprises', page),
  ]);

  const normalizedEnterprises = mapItems(enterprises.items);
  const normalizedContacts = mapItems(contacts.items);
  const normalizedProjects = mapItems(projects.items);
  const normalizedLandings = mapItems(landings.items);
  const normalizedServices = mapItems(services.items);
  const normalizedLeads = mapItems(leads.items);
  const normalizedMatches = mapItems(resourceMatches.items);
  const normalizedNeeds = mapItems(projectNeeds.items);

  for (const project of normalizedProjects) {
    project.needs = normalizedNeeds
      .filter((need) => need.projectUuid === project.uuid)
      .map((need) => need.content);
    project.matchedResourceIds = normalizedMatches
      .filter(
        (match) =>
          match.targetType === 'INVESTMENT_PROJECT' &&
          match.targetUuid === project.uuid,
      )
      .map((match) => match.resourceUuid);
  }
  for (const service of normalizedServices) {
    service.matchedResourceIds = normalizedMatches
      .filter(
        (match) =>
          match.targetType === 'ENTERPRISE_SERVICE' &&
          match.targetUuid === service.uuid,
      )
      .map((match) => match.resourceUuid);
  }
  for (const enterprise of normalizedEnterprises) {
    enterprise.contactIds = normalizedContacts
      .filter((contact) => contact.enterpriseId === enterprise.uuid)
      .map((contact) => contact.uuid);
    enterprise.projectIds = normalizedProjects
      .filter((project) => project.enterpriseId === enterprise.uuid)
      .map((project) => project.uuid);
    enterprise.landingProjectIds = normalizedLandings
      .filter((landing) => landing.enterpriseId === enterprise.uuid)
      .map((landing) => landing.uuid);
    enterprise.serviceIds = normalizedServices
      .filter((service) => service.enterpriseId === enterprise.uuid)
      .map((service) => service.uuid);
  }
  const normalizedActivities = mapItems(activities.items);
  const normalizedProfiles = mapItems(profiles.items);
  for (const activity of normalizedActivities) {
    activity.leadIds = normalizedLeads
      .filter((lead) => lead.sourceId === activity.uuid)
      .map((lead) => lead.uuid);
    activity.followed = normalizedProfiles.some(
      (profile) =>
        profile.objectType === 'activities' &&
        profile.objectUuid === activity.uuid &&
        profile.isFavorite,
    );
  }

  const normalizedResources = mapItems(resources.items);
  return {
    activities: normalizedActivities as IicState['activities'],
    leads: normalizedLeads as IicState['leads'],
    enterprises: normalizedEnterprises as IicState['enterprises'],
    contacts: normalizedContacts as IicState['contacts'],
    investmentProjects: normalizedProjects as IicState['investmentProjects'],
    landingProjects: normalizedLandings as IicState['landingProjects'],
    enterpriseServices: normalizedServices as IicState['enterpriseServices'],
    carriers: normalizedResources.filter(
      (item) => item.resourceType === 'CARRIER',
    ),
    policyFunds: normalizedResources.filter((item) =>
      ['POLICY', 'FUND'].includes(item.resourceType),
    ),
    cooperationResources: normalizedResources.filter(
      (item) => item.resourceType === 'COOPERATION',
    ),
    tasks: mapItems(tasks.items) as IicState['tasks'],
    activitiesLog: logs.items.map((item) => ({
      id: item.uuid,
      targetType: item.targetType,
      targetId: item.targetUuid,
      type: item.action,
      content: item.content,
      operator: item.operatorMemberUuid,
      createdAt: item.createdAt,
    })) as ActivityLog[],
    tenantObjectProfiles: normalizedProfiles,
    workbench,
    analytics,
    industryChains: mapItems(industryChains.items),
    industrySegments: mapItems(industrySegments.items),
    industryEnterprises: mapItems(industryEnterprises.items),
  };
};

const entityLabel = (item: BaseEntity & Record<string, unknown>) =>
  [item.name, item.status, item.stage, item.owner, item.industry, item.region]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

interface IicDataValue {
  state: IicState;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  listEntities: <T extends BaseEntity>(
    type: EntityType,
    query?: EntityQuery,
  ) => T[];
  getEntity: <T extends BaseEntity>(
    type: EntityType,
    id: string,
  ) => T | undefined;
  createEntity: (
    type: EntityType,
    entity: Partial<BaseEntity> & { name: string } & Record<string, unknown>,
  ) => Promise<void>;
  updateEntity: (
    type: EntityType,
    id: string,
    patch: Record<string, unknown>,
  ) => Promise<void>;
  createLeadFromActivity: (activityId: string) => Promise<string | undefined>;
  convertLead: (leadId: string) => Promise<void>;
  transitionInvestmentProject: (
    projectId: string,
    stage: InvestmentStage,
  ) => Promise<void>;
  addResourceToProject: (
    projectId: string,
    resourceId: string,
    feedback?: string,
  ) => Promise<void>;
  convertToLandingProject: (projectId: string) => Promise<void>;
  completeLandingProject: (
    projectId: string,
    input: LandingCompletionInput,
  ) => Promise<void>;
  completeEnterpriseService: (
    serviceId: string,
    input: ServiceCompletionInput,
  ) => Promise<void>;
}

const IicDataContext = React.createContext<IicDataValue | null>(null);

export const IicDataProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { currentUser, loading: sessionLoading } = useSession();
  const [state, setState] = React.useState<IicState>(emptyState);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const loadIdRef = React.useRef(0);
  const sessionIdentity = currentUser
    ? [currentUser.id, currentUser.memberUuid, currentUser.tenantUuid]
        .filter(Boolean)
        .join('|')
    : '';
  const canLoad = Boolean(currentUser) && !sessionLoading;

  const refresh = React.useCallback(async () => {
    const loadId = ++loadIdRef.current;

    if (!canLoad) {
      setState(emptyState());
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const nextState = await fetchState();
      if (loadId === loadIdRef.current) {
        setState(nextState);
        setError(null);
      }
    } catch (reason) {
      if (loadId === loadIdRef.current) {
        setError(
          reason instanceof Error ? reason : new Error('加载招商数据失败'),
        );
      }
      throw reason;
    } finally {
      if (loadId === loadIdRef.current) {
        setLoading(false);
      }
    }
  }, [canLoad, sessionIdentity]);

  React.useEffect(() => {
    void refresh().catch(() => undefined);

    return () => {
      loadIdRef.current += 1;
    };
  }, [refresh]);

  const value = React.useMemo<IicDataValue>(
    () => ({
      state,
      loading,
      error,
      refresh,
      listEntities: <T extends BaseEntity>(
        type: EntityType,
        query: EntityQuery = {},
      ) => {
        const source = state[type] as unknown as Array<
          T & Record<string, unknown>
        >;
        const keyword = query.keyword?.trim().toLowerCase();
        return source.filter((item) => {
          if (keyword && !entityLabel(item).includes(keyword)) return false;
          if (
            query.status &&
            item.status !== query.status &&
            item.stage !== query.status
          )
            return false;
          if (query.owner && item.owner !== query.owner) return false;
          if (query.industry && item.industry !== query.industry) return false;
          return true;
        }) as T[];
      },
      getEntity: <T extends BaseEntity>(type: EntityType, id: string) =>
        (state[type] as unknown as T[]).find((item) => item.id === id),
      createEntity: async (type, entity) => {
        const ownerMemberUuid =
          entity.ownerMemberUuid || getCurrentMemberUuid();
        await createIic(
          resourceForType[type],
          serializeRecord(type, { ...entity, ownerMemberUuid }),
        );
        await refresh();
      },
      updateEntity: async (type, id, patch) => {
        if (type === 'activities' && typeof patch.followed === 'boolean') {
          const profile = state.tenantObjectProfiles.find(
            (item) =>
              item.objectType === 'activities' && item.objectUuid === id,
          );
          if (profile) {
            await updateIic('tenant-object-profiles', profile.uuid, {
              isFavorite: patch.followed,
              version: profile.version,
            });
          } else {
            await createIic('tenant-object-profiles', {
              objectType: 'activities',
              objectUuid: id,
              isFavorite: patch.followed,
              isAdopted: false,
            });
          }
          await refresh();
          return;
        }
        const existing = (state[type] as unknown as BaseEntity[]).find(
          (item) => item.id === id,
        );
        if (!existing) throw new Error('记录不存在');
        await updateIic(resourceForType[type], id, {
          ...serializeRecord(type, patch),
          version: existing.version,
        });
        await refresh();
      },
      createLeadFromActivity: async (activityId) => {
        const result = await runIicCommand<Record<string, any>>(
          `activities/${activityId}/create-lead`,
          {},
        );
        await refresh();
        return result.uuid;
      },
      convertLead: async (leadId) => {
        const lead = state.leads.find((item) => item.id === leadId);
        if (!lead) throw new Error('线索不存在');
        await runIicCommand(`leads/${leadId}/convert`, {
          enterprise: lead.enterpriseId
            ? { uuid: lead.enterpriseId }
            : { name: lead.name.replace(/项目.*$/, '科技有限公司') },
          contact: lead.contactId
            ? { uuid: lead.contactId }
            : { name: '项目联系人', title: '项目负责人' },
          project: {
            name: lead.name,
            type: lead.type,
            region: lead.region,
            nextAction: lead.nextAction,
          },
        });
        await refresh();
      },
      transitionInvestmentProject: async (projectId, stage) => {
        const project = state.investmentProjects.find(
          (item) => item.id === projectId,
        );
        if (!project) throw new Error('招商项目不存在');
        await runIicCommand(`investment-projects/${projectId}/transition`, {
          stage: stageToCode[stage],
          version: project.version,
        });
        await refresh();
      },
      addResourceToProject: async (projectId, resourceId, feedback) => {
        await runIicCommand(
          `investment-projects/${projectId}/resource-matches`,
          {
            resourceUuid: resourceId,
            feedback,
          },
        );
        await refresh();
      },
      convertToLandingProject: async (projectId) => {
        const project = state.investmentProjects.find(
          (item) => item.id === projectId,
        );
        if (!project) throw new Error('招商项目不存在');
        await runIicCommand(
          `investment-projects/${projectId}/convert-to-landing`,
          {
            version: project.version,
          },
        );
        await refresh();
      },
      completeLandingProject: async (projectId, input) => {
        const project = state.landingProjects.find(
          (item) => item.id === projectId,
        );
        if (!project) throw new Error('落地项目不存在');
        await runIicCommand(`landing-projects/${projectId}/complete`, {
          ...input,
          version: project.version,
        });
        await refresh();
      },
      completeEnterpriseService: async (serviceId, input) => {
        const service = state.enterpriseServices.find(
          (item) => item.id === serviceId,
        );
        if (!service) throw new Error('企业服务不存在');
        await runIicCommand(`enterprise-services/${serviceId}/complete`, {
          ...input,
          version: service.version,
        });
        await refresh();
      },
    }),
    [error, loading, refresh, state],
  );

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Spin size="large" description="正在加载招商业务数据" />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ maxWidth: 720, margin: '80px auto' }}>
        <Alert
          type="error"
          showIcon
          message="招商业务数据加载失败"
          description={error.message}
          action={<Button onClick={() => void refresh()}>重新加载</Button>}
        />
      </div>
    );
  }
  return (
    <IicDataContext.Provider value={value}>{children}</IicDataContext.Provider>
  );
};

export const useIicData = (): IicDataValue => {
  const context = React.useContext(IicDataContext);
  if (!context)
    throw new Error('useIicData must be used within IicDataProvider');
  return context;
};
